import { useEffect, useState } from 'react';
import {
  App,
  Button,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Switch,
  Tabs,
  Typography,
  Alert,
} from 'antd';
import {
  profileRestrictionsApi,
  type ProfileRestrictionsDto,
} from '@/api/profileRestrictionsApi';

const { Text, Paragraph } = Typography;

interface ProfileRestrictionsModalProps {
  profileId: number | null;
  profileName?: string;
  open: boolean;
  onClose: (saved: boolean) => void;
}

/**
 * Modal de edicion completa de restricciones AMAPI de un Profile.
 * Las restricciones estan agrupadas en tabs y dentro de cada tab en collapsibles
 * para no abrumar al usuario con 50+ campos.
 *
 * El sync con Google se dispara automaticamente al guardar (backend enqueua job).
 */
export default function ProfileRestrictionsModal({
  profileId,
  profileName,
  open,
  onClose,
}: ProfileRestrictionsModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<ProfileRestrictionsDto>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [systemUpdateType, setSystemUpdateType] = useState<number>(0);

  useEffect(() => {
    if (!open || !profileId) return;
    setLoading(true);
    profileRestrictionsApi
      .get(profileId)
      .then((dto) => {
        form.setFieldsValue(dto);
        setSystemUpdateType(dto.systemUpdateType);
      })
      .catch((err) => {
        message.error('No se pudieron cargar las restricciones: ' + (err?.message ?? err));
      })
      .finally(() => setLoading(false));
  }, [open, profileId, form, message]);

  const handleSave = async () => {
    if (!profileId) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      await profileRestrictionsApi.update(profileId, values);
      message.success('Restricciones actualizadas. Sincronizando con Google...');
      onClose(true);
    } catch (err: unknown) {
      // Form validation errors no se loggean aca; el resto si
      if ((err as { errorFields?: unknown[] })?.errorFields) {
        message.warning('Hay campos invalidos. Revisa los marcados en rojo.');
      } else {
        const errorObj = err as { message?: string; response?: { data?: { message?: string } } };
        message.error(
          'Error al guardar: ' +
            (errorObj?.response?.data?.message ?? errorObj?.message ?? 'Error desconocido'),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Restricciones · ${profileName ?? 'Perfil'}`}
      open={open}
      onCancel={() => onClose(false)}
      width={900}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => onClose(false)} disabled={saving}>
          Cancelar
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Guardar y sincronizar
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Tabs
            defaultActiveKey="apps"
            items={[
              {
                key: 'apps',
                label: 'Apps & Permisos',
                children: <AppsTab />,
              },
              {
                key: 'dispositivo',
                label: 'Dispositivo',
                children: <DeviceTab />,
              },
              {
                key: 'password',
                label: 'Password & PIN',
                children: <PasswordTab />,
              },
              {
                key: 'wifi',
                label: 'Wi-Fi',
                children: <WifiTab />,
              },
              {
                key: 'frp',
                label: 'FRP',
                children: <FrpTab />,
              },
              {
                key: 'updates',
                label: 'Actualizaciones',
                children: (
                  <SystemUpdateTab
                    systemUpdateType={systemUpdateType}
                    onChange={setSystemUpdateType}
                  />
                ),
              },
              {
                key: 'reporting',
                label: 'Telemetria',
                children: <ReportingTab />,
              },
            ]}
          />
        </Form>
      </Spin>
    </Modal>
  );
}

// ============================================================================
// TAB: Apps & Permisos
// ============================================================================
function AppsTab() {
  return (
    <Collapse
      defaultActiveKey={['ctrl']}
      items={[
        {
          key: 'ctrl',
          label: 'Control de Aplicaciones',
          children: (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Modo Play Store"
                  name="playStoreMode"
                  tooltip="Whitelist: solo apps autorizadas. Blacklist: todas excepto las bloqueadas."
                >
                  <Select
                    options={[
                      { value: 0, label: 'Whitelist (recomendado)' },
                      { value: 1, label: 'Blacklist' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Politica default de permisos"
                  name="defaultPermissionPolicy"
                  tooltip="Para permisos en runtime cuando una app los solicita."
                >
                  <Select
                    options={[
                      { value: 0, label: 'Prompt (preguntar al usuario)' },
                      { value: 1, label: 'Grant (otorgar automaticamente)' },
                      { value: 2, label: 'Deny (denegar automaticamente)' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Politica de auto-update de apps"
                  name="appAutoUpdatePolicy"
                >
                  <Select
                    options={[
                      { value: 'AUTO_UPDATE_DEFAULT', label: 'Default (con WiFi y cargando)' },
                      { value: 'AUTO_UPDATE_POSTPONED', label: 'Postergado (hasta 90 dias)' },
                      { value: 'AUTO_UPDATE_HIGH_PRIORITY', label: 'Alta prioridad (recomendado)' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="El usuario puede instalar apps fuera del catalogo"
                  name="installAppsDisabled"
                  valuePropName="checked"
                  tooltip="Si esta activo (Disabled), bloquea instalar apps fuera de Managed Play."
                >
                  <Switch checkedChildren="Bloqueado" unCheckedChildren="Permitido" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="El usuario puede desinstalar apps"
                  name="uninstallAppsDisabled"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Bloqueado" unCheckedChildren="Permitido" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Permite fuentes desconocidas (sideload APK)"
                  name="installUnknownSourcesAllowed"
                  valuePropName="checked"
                  tooltip="Critico: si lo permitis el usuario puede instalar APKs por fuera de Play."
                >
                  <Switch checkedChildren="Permitido" unCheckedChildren="Bloqueado" />
                </Form.Item>
              </Col>
            </Row>
          ),
        },
        {
          key: 'comun',
          label: 'Restricciones Comunes',
          children: (
            <Row gutter={16}>
              <BoolField name="shareLocationDisabled" label="Bloquear compartir ubicacion" />
              <Col span={12}>
                <Form.Item label="Configuracion de idioma" name="configureLocale">
                  <Select
                    options={[
                      { value: 0, label: 'Permitido' },
                      { value: 1, label: 'Bloqueado' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <BoolField name="outgoingBeamDisabled" label="Bloquear NFC outgoing beam" />
              <Col span={12}>
                <Form.Item label="Impresion" name="printingPolicy">
                  <Select
                    options={[
                      { value: 0, label: 'Permitida' },
                      { value: 1, label: 'Bloqueada' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <BoolField name="screenCaptureDisabled" label="Bloquear capturas de pantalla" />
              <Col span={12}>
                <Form.Item label="Camara" name="cameraAccess">
                  <Select
                    options={[
                      { value: 0, label: 'Decision del usuario' },
                      { value: 1, label: 'Bloqueada en hardware' },
                      { value: 2, label: 'Forzada habilitada' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <BoolField name="debuggingFeaturesAllowed" label="Permitir opciones de desarrollador / ADB" />
              <BoolField name="credentialsConfigDisabled" label="Bloquear instalar certificados" />
            </Row>
          ),
        },
        {
          key: 'ubicacion',
          label: 'Ubicacion',
          children: (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Modo de ubicacion" name="locationMode">
                  <Select
                    options={[
                      { value: 0, label: 'Alta precision (GPS+WiFi+redes)' },
                      { value: 1, label: 'Solo GPS' },
                      { value: 2, label: 'Ahorro (solo redes)' },
                      { value: 3, label: 'Apagada' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          ),
        },
      ]}
    />
  );
}

// ============================================================================
// TAB: Dispositivo
// ============================================================================
function DeviceTab() {
  return (
    <Collapse
      defaultActiveKey={['critico']}
      items={[
        {
          key: 'critico',
          label: 'Restricciones criticas',
          children: (
            <Row gutter={16}>
              <BoolField name="factoryResetDisabled" label="Bloquear factory reset" criticalHint />
              <BoolField name="safeBootDisabled" label="Bloquear safe boot" />
              <BoolField name="usbFileTransferDisabled" label="Bloquear USB file transfer" />
              <BoolField name="mountPhysicalMediaDisabled" label="Bloquear montar SD card / USB" />
              <BoolField name="networkResetDisabled" label="Bloquear reset de red" />
              <BoolField name="userSwitchDisabled" label="Bloquear cambio de usuario" />
            </Row>
          ),
        },
        {
          key: 'red',
          label: 'Red & Conectividad',
          children: (
            <Row gutter={16}>
              <BoolField name="wifiConfigDisabled" label="Bloquear configuracion WiFi" />
              <BoolField name="bluetoothDisabled" label="Bluetooth deshabilitado" />
              <BoolField name="bluetoothConfigDisabled" label="Bloquear config Bluetooth" />
              <BoolField name="dataRoamingDisabled" label="Bloquear roaming de datos" />
              <BoolField name="mobileNetworksConfigDisabled" label="Bloquear config red movil" />
              <BoolField name="tetheringConfigDisabled" label="Bloquear tethering" />
              <BoolField name="cellBroadcastsConfigDisabled" label="Bloquear cell broadcasts" />
              <Col span={12}>
                <Form.Item label="Modo avion" name="airplaneModeState">
                  <Select
                    options={[
                      { value: 0, label: 'No especificado' },
                      { value: 1, label: 'Decision del usuario' },
                      { value: 2, label: 'Bloqueado (no permitido)' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
          ),
        },
        {
          key: 'comunic',
          label: 'Comunicaciones',
          children: (
            <Row gutter={16}>
              <BoolField name="outgoingCallsDisabled" label="Bloquear llamadas salientes" />
              <BoolField name="smsDisabled" label="Bloquear SMS" />
              <BoolField name="unmuteMicrophoneDisabled" label="Bloquear desmutear microfono" />
            </Row>
          ),
        },
        {
          key: 'ui',
          label: 'UI / Sistema',
          children: (
            <Row gutter={16}>
              <BoolField name="adjustVolumeDisabled" label="Bloquear ajuste de volumen" />
              <BoolField name="autoTimeRequired" label="Forzar fecha/hora automatica" />
              <BoolField name="backupServiceEnabled" label="Habilitar backup service de Google" />
              <BoolField name="funDisabled" label="Easter eggs deshabilitados" />
              <BoolField name="addUserDisabled" label="Bloquear agregar usuarios" />
              <BoolField name="modifyAccountsDisabled" label="Bloquear modificar cuentas" />
            </Row>
          ),
        },
      ]}
    />
  );
}

// ============================================================================
// TAB: Password
// ============================================================================
function PasswordTab() {
  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item label="Calidad de password" name="passwordQuality" tooltip="Tipo de password requerido">
          <Select
            options={[
              { value: 'PASSWORD_QUALITY_UNSPECIFIED', label: 'Sin requerimiento' },
              { value: 'BIOMETRIC_WEAK', label: 'Biometrico debil' },
              { value: 'SOMETHING', label: 'Algo (cualquier cosa)' },
              { value: 'NUMERIC', label: 'Numerico' },
              { value: 'NUMERIC_COMPLEX', label: 'Numerico complejo (sin secuencias)' },
              { value: 'ALPHABETIC', label: 'Alfabetico' },
              { value: 'ALPHANUMERIC', label: 'Alfanumerico' },
              { value: 'COMPLEX', label: 'Complejo' },
            ]}
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="Longitud minima"
          name="passwordMinimumLength"
          rules={[{ required: true, message: 'Requerido' }]}
        >
          <InputNumber min={4} max={16} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="Tiempo maximo hasta lock (ms)"
          name="maximumTimeToLockMs"
          tooltip="300000 ms = 5 minutos"
        >
          <InputNumber min={0} step={60000} style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="PIN esperado"
          name="expectedPin"
          tooltip="Default 0321 segun politica Global Connect. Cambiar a 3465 cuando un equipo se reporta como perdido."
          rules={[{ required: true, message: 'Requerido' }]}
        >
          <Input maxLength={16} placeholder="0321" />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Alert
          type="info"
          showIcon
          message="Sobre el PIN"
          description="AMAPI no permite forzar un PIN especifico via policy. El sistema lo maneja con un job (PinComplianceJob) que detecta cambios y emite RESET_PASSWORD al ExpectedPin de arriba. Para perdida/robo, cambia el ExpectedPin a 3465 y el dispositivo se bloqueara con ese PIN en el siguiente check-in."
        />
      </Col>
    </Row>
  );
}

// ============================================================================
// TAB: Wi-Fi
// ============================================================================
function WifiTab() {
  return (
    <>
      <Alert
        type="warning"
        showIcon
        message="La password se almacena encriptada"
        description={
          <span>
            La passphrase se guarda encriptada con AES en BD. Al cargar este formulario veras
            <Text code> *** </Text>
            si ya hay una password seteada. Para mantenerla intacta dejala asi. Para cambiarla,
            escribi la nueva password. Para limpiarla, borra todo el campo.
          </span>
        }
        style={{ marginBottom: 16 }}
      />
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="Habilitar Wi-Fi corporativa en este perfil"
            name="wifiEnabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="SSID" name="wifiSsid">
            <Input placeholder="MOB-MIR" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Seguridad" name="wifiSecurity">
            <Select
              options={[
                { value: 0, label: 'Sin seguridad (abierta)' },
                { value: 1, label: 'WEP' },
                { value: 2, label: 'WPA/WPA2/WPA3 Personal (PSK)' },
                { value: 3, label: 'WPA/WPA2 Enterprise (EAP)' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item label="Passphrase" name="wifiPassphrase">
            <Input.Password placeholder="*** = mantener actual" autoComplete="new-password" />
          </Form.Item>
        </Col>
        <BoolField name="wifiHiddenSsid" label="Red oculta" />
        <BoolField name="wifiAutoConnect" label="Conectar automaticamente" />
      </Row>
    </>
  );
}

// ============================================================================
// TAB: FRP
// ============================================================================
function FrpTab() {
  return (
    <>
      <Alert
        type="info"
        showIcon
        message="Factory Reset Protection"
        description="Las cuentas autorizadas son las unicas que pueden desbloquear un dispositivo despues de un factory reset no autorizado. Una por linea (o separadas por coma). Son los Google Account IDs numericos de 21 digitos, no los emails."
        style={{ marginBottom: 16 }}
      />
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="Habilitar Factory Reset Protection"
            name="frpEnabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label="Cuentas autorizadas (Google Account IDs, separadas por coma)"
            name="frpAccountsCsv"
          >
            <Input.TextArea
              rows={5}
              placeholder="114970162378353481499,117547665394907212293,..."
              autoComplete="off"
            />
          </Form.Item>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Tip: para obtener el Google Account ID de una cuenta, podes usar el endpoint
            People API de Google con resourceName=people/me.
          </Paragraph>
        </Col>
      </Row>
    </>
  );
}

// ============================================================================
// TAB: System Update
// ============================================================================
function SystemUpdateTab({
  systemUpdateType,
  onChange,
}: {
  systemUpdateType: number;
  onChange: (v: number) => void;
}) {
  return (
    <Row gutter={16}>
      <Col span={12}>
        <Form.Item label="Politica de actualizacion de Android" name="systemUpdateType">
          <Select
            onChange={(v) => onChange(v)}
            options={[
              { value: 0, label: 'Automatico (apenas disponible)' },
              { value: 1, label: 'Ventana horaria' },
              { value: 2, label: 'Postergar 30 dias' },
              { value: 3, label: 'Sin especificar (decide usuario)' },
            ]}
          />
        </Form.Item>
      </Col>
      {systemUpdateType === 1 && (
        <>
          <Col span={6}>
            <Form.Item
              label="Inicio de ventana (min desde 00:00)"
              name="systemUpdateStartMinutes"
              tooltip="120 = 2:00 AM"
            >
              <InputNumber min={0} max={1439} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Fin de ventana (min desde 00:00)"
              name="systemUpdateEndMinutes"
              tooltip="240 = 4:00 AM"
            >
              <InputNumber min={0} max={1439} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </>
      )}
    </Row>
  );
}

// ============================================================================
// TAB: Reporting
// ============================================================================
function ReportingTab() {
  return (
    <Row gutter={16}>
      <BoolField name="reportDeviceSettings" label="Settings del dispositivo" />
      <BoolField name="reportSoftwareInfo" label="Info de software" />
      <BoolField name="reportNetworkInfo" label="Info de red" />
      <BoolField name="reportApplicationReports" label="Reporte de apps instaladas" />
      <BoolField name="reportPowerManagementEvents" label="Eventos de bateria" />
      <BoolField name="reportHardwareStatus" label="Estado de hardware" />
      <BoolField name="reportMemoryInfo" label="Info de memoria" />
      <BoolField name="reportDisplayInfo" label="Info de display" />
      <BoolField name="reportSystemProperties" label="Propiedades del sistema" />
    </Row>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function BoolField({
  name,
  label,
  criticalHint,
}: {
  name: string;
  label: string;
  criticalHint?: boolean;
}) {
  return (
    <Col span={12}>
      <Form.Item
        label={
          <span>
            {label}
            {criticalHint && (
              <Text type="danger" style={{ marginLeft: 6, fontSize: 11 }}>
                (critico)
              </Text>
            )}
          </span>
        }
        name={name}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
    </Col>
  );
}
